import { useCallback, useReducer } from 'react';
import { CheckupInfoSlideTemplate as CheckupInfoSlideTemplate } from '../../components/CheckupInfoSlideTemplate';
import { CheckupFormSlideTemplate } from '../../components/CheckupFormSlideTemplate';
import { OrderConfirmationSlide } from '../../components/OrderConfirmationSlide';
import { PaymentSlide } from '../../components/PaymentSlide';
import { RefillsSlide } from '../../components/RefillsSlide';
import { PatientCreditCard } from '@qmd-types/patient';

type Component = () => JSX.Element;

export type FormSlideProps = (typeof formSlideConfig)[number];

const formSlideConfig = [
  {
    question:
      'Beyond mild nausea, are you having any side effects or adverse reactions to the medication?',
    // pretty weak way to segment this but the whole thing is hard-coded right now and that's all we need to know to
    // toggle to the single weight slide input type.
    isWeight: false,
  },
  {
    question: 'Are you pregnant or planning to become pregnant?',
    isWeight: false,
  },
  {
    isWeight: true,
  },
  {
    question:
      'Since your last visit, have you developed any issues with your thyroid, pancreas, gall bladder, or kidneys — or been diagnosed with cancer?',
    isWeight: false,
  },
  {
    question:
      'If you have diabetes, have you experienced hypoglycemia, had a change in your medications, or have you developed diabetic retinopathy?',
    isWeight: false,
  },
];

type State = {
  slides: Component[];
  currentSlideIndex: number;
  checkupFailed: boolean;
  creditCard?: PatientCreditCard;
};

enum ActionNames {
  NEXT_SLIDE = 'NEXT_SLIDE',
  PREV_SLIDE = 'PREV_SLIDE',
  SET_SLIDES = 'SET_SLIDES',
  GO_TO_SLIDE = 'GO_TO_SLIDE',
  SET_CREDIT_CARD = 'SET_CREDIT_CARD',
  TOGGLE_CHECKUP_FAILED = 'TOGGLE_CHECKUP_FAILED',
}

type Action =
  | { type: ActionNames.NEXT_SLIDE }
  | { type: ActionNames.PREV_SLIDE }
  | { type: ActionNames.SET_CREDIT_CARD; data: PatientCreditCard }
  | { type: ActionNames.TOGGLE_CHECKUP_FAILED };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case ActionNames.NEXT_SLIDE: {
      return {
        ...state,
        currentSlideIndex: Math.min(
          state.currentSlideIndex + 1,
          state.slides.length - 1,
        ),
      };
    }
    case ActionNames.PREV_SLIDE: {
      return {
        ...state,
        currentSlideIndex: Math.max(state.currentSlideIndex - 1, 0),
      };
    }

    case ActionNames.SET_CREDIT_CARD: {
      return {
        ...state,
        creditCard: action.data,
      };
    }

    case ActionNames.TOGGLE_CHECKUP_FAILED: {
      return {
        ...state,
        checkupFailed: !state.checkupFailed,
      };
    }
    default:
      return state;
  }
}

// !!This is a little ugly but it works fine.  Also, because formSlides is defined outside the render method, the
// functions are not recreated on each render, and thus don't remount/lose state etc each render, which is why inlining
// functions returning JSX is usually frowned upon. if we get more complex, calculate the props inside each component by
// looking at the length of the slideConfig and adjusting for the start slide. I just wanted to force the accurate props
// without the adjustment in case we added more initial slides or intermediate slides, etc.

const formSlides = formSlideConfig.map((slideData) => () => (
  <CheckupFormSlideTemplate {...slideData} />
));

const initialState: State = {
  slides: [
    RefillsSlide,
    CheckupInfoSlideTemplate,
    ...formSlides,
    PaymentSlide,
    OrderConfirmationSlide,
  ],
  currentSlideIndex: 0,
  creditCard: undefined,
  checkupFailed: false,
};

export type UseRefillCheckupStateReturn = ReturnType<
  typeof useRefillCheckupState
>;

export function useRefillCheckupState() {
  const [state, dispatch] = useReducer(reducer, initialState);

  const nextSlide = useCallback(
    () => dispatch({ type: ActionNames.NEXT_SLIDE }),
    [dispatch],
  );

  const prevSlide = useCallback(
    () => dispatch({ type: ActionNames.PREV_SLIDE }),
    [dispatch],
  );

  const setCreditCard = useCallback(
    (creditCard: PatientCreditCard) =>
      dispatch({
        data: creditCard,
        type: ActionNames.SET_CREDIT_CARD,
      }),
    [dispatch],
  );

  const toggleCheckupFailed = useCallback(
    () => dispatch({ type: ActionNames.TOGGLE_CHECKUP_FAILED }),
    [dispatch],
  );

  return {
    ...state,
    nextSlide,
    prevSlide,
    setCreditCard,
    toggleCheckupFailed,
  };
}
